import {
  partsServiceMissingMessage,
  type CompletePunchoutInput,
  type PartsOrderActionInput,
  type PartsOrderValidation,
  type PartsOrderingService,
  type PunchoutSessionHandle,
  type StartPunchoutInput,
  type SubmitPartsOrderInput,
  type SupplierPurchaseOrderRecord,
} from '@forq/parts-data';

/**
 * Der Bestelldienst des FORQ-Servers.
 *
 * Warum es diese Datei gibt: Zugangsdaten von TVH und TREX duerfen den Browser nie
 * erreichen. Also ruft der Browser nicht den Lieferanten, sondern den eigenen Server
 * -- auf DERSELBEN Herkunft, mit der angemeldeten Sitzung, ohne jedes Token im
 * JavaScript. Der Server haelt die Zugangsdaten in `host.json`, spricht mit dem
 * Lieferanten und schreibt das Ergebnis ueber die `service_role`-Funktionen fort,
 * die dem Browser entzogen sind.
 *
 * Dasselbe Muster wie `reportServer.ts` (VITE_RENDER_TARGET=server), und aus
 * demselben Grund.
 *
 *   VITE_PARTS_TARGET=server   Eigener Windows-Server. Die Anwendung wird von ihm
 *                              ausgeliefert, also genuegt ein relativer Pfad.
 *
 *   (nicht gesetzt)            Cloud-Fassung. `available` ist false: Entwurf,
 *                              Bestandspruefung und Historie funktionieren, das
 *                              Absenden nicht -- und die Oberflaeche sagt das.
 */

const PARTS_TARGET = ((import.meta.env.VITE_PARTS_TARGET as string | undefined) ?? '').trim();

const AUF_EIGENEM_SERVER = PARTS_TARGET === 'server';

/** Läuft der Bestelldienst? Nur dann die Knöpfe zeigen, die ihn brauchen. */
export function partsServerConfigured(): boolean {
  return AUF_EIGENEM_SERVER;
}

/** Wohin die Anfrage geht -- für Fehlermeldungen und Tests nachvollziehbar. */
export function partsEndpoint(path: string): string {
  return `/ersatzteile/${path}`;
}

/**
 * Die Antwortform des Servers.
 *
 * Fachliche Fehlschläge kommen mit 200 und `ok:false` zurück -- dieselbe Entscheidung wie bei
 * der Einladungsfunktion: bei einem 4xx würde die Klartextmeldung unterwegs verloren gehen,
 * und gerade bei einer abgelehnten Rechteprüfung IST die Meldung die ganze Auskunft.
 */
interface ServerResponse<T> {
  readonly ok: boolean;
  readonly message?: string;
  readonly data?: T;
}

async function call<T>(path: string, body: Readonly<Record<string, unknown>>): Promise<T> {
  if (!AUF_EIGENEM_SERVER) throw new Error(partsServiceMissingMessage);

  let response: Response;
  try {
    response = await fetch(partsEndpoint(path), {
      method: 'POST',
      // Die Berechtigung hängt am Sitzungscookie, nicht an einem Token im JavaScript.
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Der FORQ-Server ist nicht erreichbar. Die Bestellung wurde nicht abgesendet.');
  }

  if (response.status === 401) {
    throw new Error('Die Anmeldung ist abgelaufen. Bitte neu anmelden.');
  }
  if (!response.ok) {
    throw new Error(`Der Bestelldienst antwortete mit ${response.status}.`);
  }

  const payload = (await response.json()) as ServerResponse<T>;
  if (!payload.ok || payload.data === undefined) {
    throw new Error(payload.message || 'Der Bestelldienst hat den Vorgang abgelehnt.');
  }
  return payload.data;
}

/**
 * Die fünf Schritte, die nur der Server gehen kann.
 *
 * Fällt einer davon aus, liegt das nie an fehlenden Zugangsdaten im Browser -- die gibt es
 * dort nicht und soll es nicht geben.
 */
export const partsOrderingService: PartsOrderingService = {
  get available(): boolean {
    return AUF_EIGENEM_SERVER;
  },

  startPunchout(input: StartPunchoutInput): Promise<PunchoutSessionHandle> {
    return call<PunchoutSessionHandle>('punchout/start', {
      tenantId: input.tenantId,
      workOrderId: input.workOrderId,
      provider: input.provider,
      shortfallPositionIds: input.shortfallPositionIds ?? [],
    });
  },

  completePunchout(input: CompletePunchoutInput): Promise<SupplierPurchaseOrderRecord> {
    // Nur der Übungslieferant nimmt diesen Weg. Ein echter Lieferant schickt seinen
    // Warenkorb an /ersatzteile/punchback, ohne den Browser.
    return call<SupplierPurchaseOrderRecord>('punchout/uebungsrueckgabe', {
      tenantId: input.tenantId,
      sessionToken: input.sessionToken,
      lines: input.lines,
    });
  },

  validate(input: PartsOrderActionInput): Promise<PartsOrderValidation> {
    return call<PartsOrderValidation>('pruefen', {
      tenantId: input.tenantId,
      purchaseOrderId: input.purchaseOrderId,
    });
  },

  submit(input: SubmitPartsOrderInput): Promise<SupplierPurchaseOrderRecord> {
    return call<SupplierPurchaseOrderRecord>('absenden', {
      tenantId: input.tenantId,
      purchaseOrderId: input.purchaseOrderId,
      idempotencyKey: input.idempotencyKey,
    });
  },

  refreshStatus(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord> {
    return call<SupplierPurchaseOrderRecord>('status', {
      tenantId: input.tenantId,
      purchaseOrderId: input.purchaseOrderId,
    });
  },
};
